import { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker, Polygon, Polyline } from '@react-google-maps/api';
import type { GeoJSONStoreFeatures } from 'terra-draw';

import { DrawMode, useTerraDraw } from 'hooks/useTerraDraw';
import { GeoreferenceFormValuesCoordinate } from 'utils/modules/georeference';
import { Georeference } from 'apollo/schema/types';
import { markerTypes, polygonTypes, polylineTypes } from 'utils/map';

const { google } = window;

const blue = '#4d58c8';
const red = '#bf3638';
const iconNormal = '/assets/images/marker-default.png';
const iconNormalDot = '/assets/images/marker-default-dot.png';
const iconSelected = '/assets/images/marker-blue.png';
const iconSelectedDot = '/assets/images/marker-blue-dot.png';

type MapCoordinate = { lat: number; lng: number };

/** A georeference whose coordinates have been formatted for google maps. */
export type MapGeoreference = Omit<Georeference, 'data'> & {
  data: { coordinates: MapCoordinate[] };
  isSelected?: boolean;
  onSelect?: (selected: boolean) => void;
};

function georefIcon(georef: MapGeoreference) {
  if (georef.dataType === 'CENTER') {
    if (georef.isSelected) return iconSelectedDot;
    return iconNormalDot;
  }

  if (georef.isSelected) return iconSelected;
  return iconNormal;
}

// Extract marker components from georeferences array
function getMapMarkers(georeferences: MapGeoreference[], clickable: boolean) {
  return georeferences
    .filter(georef => markerTypes.includes(georef.dataType))
    .map(georef => (
      <Marker
        key={georef.id}
        position={georef.data.coordinates[0]}
        options={{ clickable }}
        icon={georefIcon(georef)}
        onMouseOver={() => georef.onSelect?.(true)}
        onMouseOut={() => georef.onSelect?.(false)}
      />
    ));
}

// Extract polygon components from georeferences array
function getMapPolygons(georeferences: MapGeoreference[], clickable: boolean) {
  const polygonOptions = {
    fillColor: 'red',
    fillOpacity: 0.1,
    strokeColor: 'red',
    strokeWeight: 4,
    clickable,
  };

  return georeferences
    .filter(item => polygonTypes.includes(item.dataType))
    .map(item => {
      const options = {
        ...polygonOptions,
        strokeColor: item.isSelected ? blue : red,
        fillColor: item.isSelected ? blue : red,
      };

      return (
        <Polygon
          key={item.id}
          path={item.data.coordinates}
          options={options}
          onMouseOver={() => item.onSelect?.(true)}
          onMouseOut={() => item.onSelect?.(false)}
        />
      );
    });
}

// Extract polyline components from georeferences array
function getMapPolylines(georeferences: MapGeoreference[], clickable: boolean) {
  return georeferences
    .filter(item => polylineTypes.includes(item.dataType))
    .map(item => (
      <Polyline
        key={item.id}
        path={item.data.coordinates}
        options={{
          strokeColor: item.isSelected ? blue : red,
          strokeWeight: 4,
          clickable,
        }}
        onMouseOver={() => item.onSelect?.(true)}
        onMouseOut={() => item.onSelect?.(false)}
      />
    ));
}

// GeoJSON positions are [lng, lat]; our API stores { latitude, longitude }.
function positionsToCoordinates(
  positions: number[][],
): GeoreferenceFormValuesCoordinate[] {
  return positions.map(([lng, lat]) => ({ latitude: lat, longitude: lng }));
}

function featureToCoordinates(
  feature: GeoJSONStoreFeatures,
): GeoreferenceFormValuesCoordinate[] | null {
  const geometry = feature.geometry;
  switch (geometry.type) {
    case 'Point': {
      const [lng, lat] = geometry.coordinates;
      return [{ latitude: lat, longitude: lng }];
    }
    case 'LineString':
      return positionsToCoordinates(geometry.coordinates);
    case 'Polygon': {
      const ring = geometry.coordinates[0] ?? [];
      // GeoJSON rings repeat the first vertex at the end - drop it.
      const open = ring.length > 1 ? ring.slice(0, -1) : ring;
      return positionsToCoordinates(open);
    }
    default:
      return null;
  }
}

export type MapEditorProps = {
  georeferences: MapGeoreference[];
  /** Active TerraDraw mode derived from the data type, or null to disable drawing. */
  drawMode: DrawMode | null;
  onDrawingComplete: (
    coordinates: GeoreferenceFormValuesCoordinate[] | null,
  ) => void;
};

function MapEditor({
  georeferences,
  drawMode,
  onDrawingComplete,
}: MapEditorProps) {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const draw = useTerraDraw(map);

  // Keep the latest callback in a ref so the listener effect can subscribe once
  // per TerraDraw instance instead of re-subscribing on every render (the
  // parent passes a fresh function identity each time).
  const onDrawingCompleteRef = useRef(onDrawingComplete);
  useEffect(() => {
    onDrawingCompleteRef.current = onDrawingComplete;
  }, [onDrawingComplete]);

  // Emit drawn geometry, keeping only a single feature on the map.
  useEffect(() => {
    if (!draw) return;

    const handleFinish = (id: GeoJSONStoreFeatures['id']) => {
      // Read the exact feature that was just drawn/edited by id, rather than
      // relying on snapshot ordering, so we emit the right coordinates.
      const feature =
        id !== undefined ? draw.getSnapshotFeature(id) : undefined;
      onDrawingCompleteRef.current(
        feature ? featureToCoordinates(feature) : null,
      );

      // Keep only that feature on the map.
      const stale = draw
        .getSnapshot()
        .map(f => f.id)
        .filter(
          (featureId): featureId is string | number =>
            featureId !== undefined && featureId !== id,
        );
      if (stale.length) draw.removeFeatures(stale);
    };

    const handleChange = (_ids: unknown, type: string) => {
      // The only feature was removed (e.g. cleared) - reset the form value.
      if (type === 'delete' && draw.getSnapshot().length === 0) {
        onDrawingCompleteRef.current(null);
      }
    };

    draw.on('finish', handleFinish);
    draw.on('change', handleChange);
    return () => {
      draw.off('finish', handleFinish);
      draw.off('change', handleChange);
    };
  }, [draw]);

  // Switch TerraDraw mode when the selected data type changes, clearing any
  // in-progress drawing so each georeference starts fresh. The container's own
  // effect resets the form's `data` when the data type changes, so we
  // deliberately don't call onDrawingComplete here (doing so re-triggered this
  // effect via an unstable callback identity and pinned the CPU at 100%).
  useEffect(() => {
    if (!draw) return;
    draw.clear();
    draw.setMode(drawMode ?? 'static');
  }, [draw, drawMode]);

  // When not drawing, allow clicking the georef to view the infowindow
  const clickable = drawMode === null;

  const markers = getMapMarkers(georeferences, clickable);
  const polygons = getMapPolygons(georeferences, clickable);
  const polylines = getMapPolylines(georeferences, clickable);

  const handleLoad = useCallback(
    (loadedMap: google.maps.Map) => {
      setMap(loadedMap);

      if (!georeferences.length) {
        loadedMap.setCenter({ lat: 0, lng: 0 });
        loadedMap.setZoom(1);
        return;
      }

      if (georeferences.length === 1) {
        const center = georeferences[0].data.coordinates[0];
        loadedMap.setCenter(center);
        loadedMap.setZoom(11);
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      georeferences.forEach(georef => {
        georef.data.coordinates.forEach(coords => bounds.extend(coords));
      });
      loadedMap.fitBounds(bounds);
    },
    [georeferences],
  );

  const handleUnmount = useCallback(() => setMap(null), []);

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Preview</h3>

      <GoogleMap
        onLoad={handleLoad}
        onUnmount={handleUnmount}
        mapContainerStyle={{ width: '100%', height: '400px' }}
        mapTypeId={google.maps.MapTypeId.HYBRID}
      >
        {markers}
        {polygons}
        {polylines}
      </GoogleMap>
    </div>
  );
}

export default MapEditor;
